import { useEffect, useState } from "react";
import { CalendarClock, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { BRAND_COLORS } from "../../lib/bookingStatus";
import { deDateMedium, deDateTime } from "../../lib/dateFormat";

/**
 * Feb 2026 · User-Directive „Datumsänderung mit Protokoll" (5 Sep 2026).
 * Zeigt eine chronologische Liste aller Termin-Verschiebungen dieser
 * Buchung — Name, Zeitpunkt, alter → neuer Zeitraum. Standard-mäßig
 * eingeklappt; öffnet sich per Klick auf den Header.
 */
function formatDate(iso) {
  if (!iso) return "";
  try {
    return deDateTime(iso);
  } catch { return iso; }
}

function formatDay(iso) {
  if (!iso) return "—";
  try {
    return deDateMedium(iso);
  } catch { return iso; }
}

export function BookingDateAuditPanel({ bookingId, refreshToken }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!bookingId) return;
    let mounted = true;
    api.get(`/bookings/${bookingId}/date-audit`)
      .then((r) => { if (mounted) setRows(r.data || []); })
      .catch(() => { if (mounted) setRows([]); });
    return () => { mounted = false; };
  }, [bookingId, refreshToken]);

  const count = rows?.length ?? 0;
  return (
    <div className="mt-4" data-testid="booking-date-audit-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open ? "true" : "false"}
        data-testid="booking-date-audit-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-navy/10 bg-navy/[0.02] hover:bg-navy/5 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
          <div>
            <div className="label-caps text-navy-dark/75">Termin-Verschiebungen</div>
            <div className="text-[11.5px] text-navy-dark/55 mt-0.5">
              {count === 0
                ? "Noch keine Terminänderungen protokolliert."
                : `${count} Änderung${count === 1 ? "" : "en"} · chronologisch, neueste zuerst`}
            </div>
          </div>
        </div>
        <ChevronDown
          className="w-4 h-4 text-navy-dark/50 transition-transform shrink-0"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          strokeWidth={1.6}
        />
      </button>
      {open && rows && rows.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid="booking-date-audit-list">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex items-start gap-3 p-3 bg-white border border-navy/10 text-[12.5px]"
              data-testid={`booking-date-audit-row-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-navy">
                  <span className="font-medium">{r.actor_name || (r.actor_role === "admin" ? "Admin" : "Concierge")}</span>
                  <span className="text-navy-dark/50"> · </span>
                  <span className="text-navy-dark/75">Termin verschoben</span>
                </div>
                <div className="text-[11.5px] text-navy-dark/60 mt-0.5 tabular-nums">
                  {formatDay(r.old_from)} → {formatDay(r.old_to)}
                  <span className="text-navy-dark/40 mx-1.5">→</span>
                  <span className="text-navy">{formatDay(r.new_from)} → {formatDay(r.new_to)}</span>
                </div>
              </div>
              <div className="text-[11px] text-navy-dark/55 shrink-0 tabular-nums">
                {formatDate(r.at)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
